import React, { useEffect, useState } from 'react';
import { updateAnnotation, deleteAnnotation } from '../../annotations/annotationStore';
import Tooltip from '../Tooltip';
import Icon from '../Icon';
import { formatTime, showVideoAt } from './momentTime';
import TimeRange from './TimeRange';
import SenseCount from '../../annotations/SenseCount';
import { describeSense, distinguishingLabel } from '../../yasna/senseLabel';
import { useTimeline } from './timelineContext';
import { rolesWith } from '../../learner/meaning';
import ReferencePicker from './ReferencePicker';
import { hasReference } from '../../utils/reference';
import {
  nextStartAfter, subtitleFor, isAlignedWith, subtitlesAround, hasEnd, endOf,
} from '../../utils/timeline';

// "imite — imitates": word and meaning, no grammar (Requirement 56).
export { describeSense } from '../../yasna/senseLabel';

/**
 * AnnotationRow — one subtitle-free annotation in the teacher's list: its
 * start time, the annotated word and its meaning (looked up in yasna), with
 * edit and delete.
 *
 * Editing changes the word, the start → end window and the linked yasna
 * sense, picked from the word's meanings (looked up again as the word is
 * changed).
 *
 * Props:
 *   annotation — the annotation document ({ id, surfaceText, senseId, startTime, endTime, ... })
 *   yasna — the loaded yasna module, or null while it loads
 *   language — 'fr' | 'de', the language yasna is searched in
 *   currentTime — playback time, for the "use current time" buttons
 *   onUpdated(annotation) / onDeleted(id)
 *   onEditingChange(editing) — the row is open for editing or deleting
 *   untimed — a title annotation: no times, no moving between subtitles
 *   reorder — optional { up, down }: move it among its subtitle's words
 *             (null at either end; Requirement 58)
 */
// A subtitle's window as an annotation's times.
const timesOf = (subtitle) => ({
  startTime: subtitle.startTime,
  endTime: endOf(subtitle),
});

function AnnotationRow({ annotation, yasna, language, currentTime, onUpdated, onDeleted, onEditingChange, untimed = false, reorder }) {
  const { subtitles } = useTimeline();
  const [sense, setSense] = useState(null);
  const [aligning, setAligning] = useState(false);
  const [mode, setMode] = useState('view'); // 'view' | 'edit' | 'delete' | 'ref'

  useEffect(() => {
    onEditingChange && onEditingChange(mode !== 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Look up the meaning of the linked sense.
  useEffect(() => {
    if (!yasna || !annotation.senseId) return undefined;
    let cancelled = false;
    yasna.getById(annotation.senseId).then(
      (s) => { if (!cancelled) setSense(s); },
      () => { /* keep showing the word alone */ }
    );
    return () => { cancelled = true; };
  }, [yasna, annotation.senseId]);

  const seek = () => showVideoAt(annotation.startTime); // paused there

  // The role the word plays in this sentence, when its sense's literal
  // gloss depends on it: vous as an indirect object is "to you"
  // (Requirement 61). Chosen here; the glosses are yasna's.
  const roles = sense ? rolesWith(yasna, sense).filter((r) => r.role) : [];
  const [savingRole, setSavingRole] = useState(false);
  const chooseRole = async (role) => {
    setSavingRole(true);
    try {
      onUpdated && onUpdated(await updateAnnotation(annotation, { role: role || null }));
    } catch (e) {
      console.error('Error saving the role:', e);
    }
    setSavingRole(false);
  };

  // The subtitle the annotation is listed under, and the ones before and
  // after it: ‹ › move the annotation there. An annotation with its
  // subtitle's times shows no times of its own (the subtitle shows them);
  // one whose times differ shows them, with a link button to align it.
  const { own, prev, next } = untimed ? {} : subtitlesAround(annotation.startTime, subtitles);
  const aligned = untimed || (!!own && isAlignedWith(annotation, own));

  // Give the annotation a subtitle's times: align with its own, or move it.
  const moveTo = async (target) => {
    setAligning(true);
    try {
      // In another subtitle it goes after that subtitle's words; aligning
      // with its own keeps its place (Requirement 58).
      const changes = timesOf(target);
      if (typeof annotation.order === 'number' && target !== own) changes.order = null;
      onUpdated && onUpdated(await updateAnnotation(annotation, changes));
    } catch (e) {
      console.error('Error moving annotation:', e);
    }
    setAligning(false);
  };

  const handleDelete = async () => {
    try {
      await deleteAnnotation(annotation);
      onDeleted && onDeleted(annotation.id);
    } catch (e) {
      console.error('Error deleting annotation:', e);
      setMode('view');
    }
  };

  if (mode === 'delete') {
    return (
      <li className="moment-annotator-item">
        <span className="moment-annotator-confirm">Delete "{annotation.surfaceText}"?</span>
        <button type="button" className="moment-annotator-del-yes" onClick={handleDelete}>Delete</button>
        <button type="button" className="moment-annotator-del-no" onClick={() => setMode('view')}>Cancel</button>
      </li>
    );
  }

  if (mode === 'ref') {
    return (
      <li className="moment-annotator-item moment-annotator-item--editing">
        <ReferencePicker
          annotation={annotation}
          subtitles={subtitles}
          onSave={async (refersTo) => {
            const updated = await updateAnnotation(annotation, { refersTo });
            setMode('view');
            onUpdated && onUpdated(updated);
          }}
          onCancel={() => setMode('view')}
        />
      </li>
    );
  }

  if (mode === 'edit') {
    return (
      <li className="moment-annotator-item moment-annotator-item--editing">
        <AnnotationEditor
          annotation={annotation}
          untimed={untimed}
          currentSense={sense}
          yasna={yasna}
          language={language}
          currentTime={currentTime}
          onSaved={(updated, newSense) => {
            setSense(newSense);
            setMode('view');
            onUpdated && onUpdated(updated);
          }}
          onCancel={() => setMode('view')}
        />
      </li>
    );
  }

  return (
    <li className="moment-annotator-item">
      {reorder && (
        // Its place among the subtitle's words, as students see them
        <span className="annotation-order">
          <button type="button" className="annotation-order-btn" onClick={reorder.up} disabled={!reorder.up}
            aria-label={`Move ${annotation.surfaceText} up`} title="Move up">
            <Icon name="chevron-up" size={13} />
          </button>
          <button type="button" className="annotation-order-btn" onClick={reorder.down} disabled={!reorder.down}
            aria-label={`Move ${annotation.surfaceText} down`} title="Move down">
            <Icon name="chevron-down" size={13} />
          </button>
        </span>
      )}
      <button
        type="button"
        className="moment-annotator-seek"
        onClick={seek}
        aria-label={`Go to ${formatTime(annotation.startTime)}: ${annotation.surfaceText}`}
      >
        {!aligned && (
          <span className="moment-annotator-time">
            {formatTime(annotation.startTime)} → {hasEnd(annotation) ? formatTime(annotation.endTime) : 'next'}
          </span>
        )}
        <span className="moment-annotator-text">{annotation.surfaceText}</span>
        <span className="moment-annotator-sense">{sense ? describeSense(sense) : ''}</span>
        {hasReference(annotation) && (
          // What it stands for here (Requirement 62)
          <span className="annotation-ref">→ “{annotation.refersTo.text}”</span>
        )}
        <SenseCount senseId={annotation.senseId} />
      </button>
      {roles.length > 1 && (
        <select
          className="annotation-role"
          value={roles.some((r) => r.role === annotation.role) ? annotation.role : ''}
          onChange={(e) => chooseRole(e.target.value)}
          disabled={savingRole}
          aria-label={`Role of ${annotation.surfaceText} here, for the word-for-word line`}
          title="The role this word plays in the sentence: it decides what it says literally (vous as an indirect object → “to you”)"
        >
          <option value="">role…</option>
          {roles.map((r) => (
            <option key={r.role} value={r.role}>{`as ${r.role} → ${r.gloss}`}</option>
          ))}
        </select>
      )}
      {!untimed && subtitles.length > 0 && (
        <>
          <Tooltip label={prev ? `Move to the previous subtitle (${formatTime(prev.startTime)})` : 'No previous subtitle'}>
            <button type="button" className="icon-btn icon-btn--sm annotation-move-btn" onClick={() => moveTo(prev)}
              disabled={!prev || aligning} aria-label={`Move ${annotation.surfaceText} to the previous subtitle`}>
              <Icon name="chevron-left" size={15} />
            </button>
          </Tooltip>
          <Tooltip label={next ? `Move to the next subtitle (${formatTime(next.startTime)})` : 'No next subtitle'}>
            <button type="button" className="icon-btn icon-btn--sm annotation-move-btn" onClick={() => moveTo(next)}
              disabled={!next || aligning} aria-label={`Move ${annotation.surfaceText} to the next subtitle`}>
              <Icon name="chevron-right" size={15} />
            </button>
          </Tooltip>
        </>
      )}
      {own && !aligned && (
        <Tooltip label={`Align with its subtitle (${formatTime(own.startTime)}): same start and end`}>
          <button type="button" className="icon-btn icon-btn--sm" onClick={() => moveTo(own)} disabled={aligning}
            aria-label={`Align ${annotation.surfaceText} with its subtitle`}>
            <Icon name="link" size={15} />
          </button>
        </Tooltip>
      )}
      {!untimed && subtitles.length > 0 && (
        <Tooltip label={hasReference(annotation) ? `Refers to “${annotation.refersTo.text}”: change` : 'Link to what it refers to (en → les pommes)'}>
          <button type="button" className={`icon-btn icon-btn--sm${hasReference(annotation) ? ' annotation-ref-btn--on' : ''}`}
            onClick={() => setMode('ref')}
            aria-label={`What ${annotation.surfaceText} refers to`}>
            <Icon name="arrow-right" size={15} />
          </button>
        </Tooltip>
      )}
      <Tooltip label="Edit annotation">
        <button type="button" className="icon-btn icon-btn--sm" onClick={() => setMode('edit')}
          aria-label={`Edit annotation ${annotation.surfaceText}`}>
          <Icon name="pencil" size={15} />
        </button>
      </Tooltip>
      <Tooltip label="Delete annotation">
        <button type="button" className="icon-btn icon-btn--sm icon-btn--danger" onClick={() => setMode('delete')}
          aria-label={`Delete annotation ${annotation.surfaceText}`}>
          <Icon name="x" size={15} />
        </button>
      </Tooltip>
    </li>
  );
}

// Wait this long after the last keystroke before searching yasna.
const SEARCH_DELAY = 400;

/** The inline edit form: word, From/To, and the meaning it links to. */
function AnnotationEditor({ annotation, untimed, currentSense, yasna, language, currentTime, onSaved, onCancel }) {
  const [text, setText] = useState(annotation.surfaceText);
  const [startSeconds, setStartSeconds] = useState(annotation.startTime);
  const { subtitles, annotations } = useTimeline();
  const [endSeconds, setEndSeconds] = useState(annotation.endTime ?? null);
  const alignTo = untimed ? null : subtitleFor(startSeconds, subtitles);
  const [senseId, setSenseId] = useState(annotation.senseId);
  const [senses, setSenses] = useState(currentSense ? [currentSense] : []);
  const [searched, setSearched] = useState('');
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const search = async (term) => {
    const q = term.trim();
    if (!yasna || !q) return;
    setSearching(true);
    setError('');
    try {
      setSenses(await yasna.search(q, language));
      setSearched(q);
    } catch (e) {
      setError("Couldn't reach the dictionary. Please try again.");
    }
    setSearching(false);
  };

  // Offer the word's meanings straight away, and look them up again as the
  // word is changed (once typing pauses).
  useEffect(() => {
    const q = text.trim();
    if (!q || q === searched) return undefined;
    const timer = setTimeout(() => search(q), q === annotation.surfaceText.trim() ? 0 : SEARCH_DELAY);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yasna, text]);

  const setWindow = ({ start, end }) => {
    setStartSeconds(start);
    setEndSeconds(end);
  };

  const chosen = senses.find((s) => s.word_sense_id === senseId) || null;
  const wordChanged = text.trim() !== searched;
  const canSave = text.trim() && chosen && !wordChanged && !saving;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError('');
    const changes = {
      surfaceText: text.trim(),
      // A title annotation has no times to change.
      ...(untimed ? {} : { startTime: startSeconds, endTime: endSeconds }),
      senseId: chosen.word_sense_id,
      language: chosen.language,
      // Another sense has other roles: choose again.
      ...(chosen.word_sense_id !== annotation.senseId ? { role: null } : {}),
    };
    try {
      onSaved(await updateAnnotation(annotation, changes), chosen);
    } catch (err) {
      console.error('Error updating annotation:', err);
      setError('Failed to save the changes. Please try again.');
      setSaving(false);
    }
  };

  return (
    <form className="annotation-editor" onSubmit={handleSave} aria-label={`Edit annotation ${annotation.surfaceText}`}>
      <div className="subtitle-form-field">
        <label className="subtitle-form-label" htmlFor={`edit-text-${annotation.id}`}>Word or phrase</label>
        <input
          id={`edit-text-${annotation.id}`}
          className="subtitle-form-input"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoCapitalize="off"
          spellCheck="false"
        />
      </div>

      {!untimed && (
        <>
          <TimeRange
            start={startSeconds}
            end={endSeconds}
            onChange={setWindow}
            currentTime={currentTime}
            optionalEnd
            nextStart={nextStartAfter(startSeconds, subtitles, annotations.filter((a) => a.id !== annotation.id))}
            testIdPrefix="edit"
          />
          {alignTo && !isAlignedWith({ startTime: startSeconds, endTime: endSeconds }, alignTo) && (
            <button type="button" className="annotation-editor-align"
              onClick={() => setWindow({ start: alignTo.startTime, end: timesOf(alignTo).endTime })}>
              <Icon name="link" size={14} /> Align with the subtitle at {formatTime(alignTo.startTime)}
            </button>
          )}
        </>
      )}

      <span className="subtitle-form-label">Meaning</span>
      {!yasna && <p className="moment-annotator-status">Loading dictionary…</p>}
      {yasna && !searching && searched && senses.length === 0 && (
        <div className="sense-picker-missing">
          <p>"{searched}" isn't in the dictionary yet.</p>
          <a href={yasna.teacherUrl(searched, language)} target="_blank" rel="noreferrer">Add it in Yasna ↗</a>
        </div>
      )}
      {senses.length > 0 && (
        <ul className="sense-picker-results" aria-label="Dictionary senses">
          {senses.map((s) => (
            <li key={s.word_sense_id}>
              <button
                type="button"
                className={`sense-picker-row ${s.word_sense_id === senseId ? 'sense-picker-row--selected' : ''}`}
                aria-pressed={s.word_sense_id === senseId}
                onClick={() => setSenseId(s.word_sense_id)}
              >
                <span className="sense-picker-word">{s.word}</span>
                {distinguishingLabel(s, senses) && <span className="sense-picker-label">{distinguishingLabel(s, senses)}</span>}
                {s.short_gloss && <span className="sense-picker-gloss">{s.short_gloss}</span>}
                    <SenseCount senseId={s.word_sense_id} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {yasna && wordChanged && text.trim() && (
        <p className="sense-picker-hint">Looking up "{text.trim()}"…</p>
      )}

      {error && <p className="subtitle-form-error">{error}</p>}

      <div className="subtitle-form-actions">
        <button type="submit" className="subtitle-form-save-btn" disabled={!canSave}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="subtitle-form-cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default AnnotationRow;
